import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { SettingsService } from '../settings.service';

export enum ProductAnalyticsEvent {
  PageView = 'web_view',
  GrabJob = 'web_grab_job',
  GrabJobExit = 'web_grab_job_exit',
  GrabJobModalContinue = 'web_grab_job_modal_continue',
  GrabJobModalSummary = 'web_grab_job_modal_summary',
  JobDetailsView = 'web_job_details_view',
  HideJobInteraction = 'web_hide_job',
  JobFilterApply = 'web_job_filter_apply',
  JobSearch = 'web_job_search',
  WatchJob = 'web_watch_job',
  CreateOrderVehicleStepContinue = 'web_create_order_vehicle_step_continue',
  CreateOrderRouteTimingsContinue = 'web_create_order_route_timings_continue',
  CreateOrderReviewContinue = 'web_create_order_review_continue',
  OrderPriceSummaryView = 'web_order_price_summary_view',
  NewTaskOrder = 'web_new_task_order',
  EditTaskOrder = 'web_edit_task_order',
  OrderPriceSummaryExit = 'web_order_price_summary_exit',
  OrderListSummaryView = 'web_order_list_summary_view',
  OrderPriceToastSuccess = 'web_order_price_toast_success',
  RoutesFilterApply = 'web_routes_filter_apply',
  RouteSearch = 'web_route_search',
  WatchJobRoute = 'web_watch_route',
  HideJobRoute = 'web_hide_route',
  GrabRoute = 'web_grab_route',
  GrabRouteModalExit = 'web_grab_route_modal_exit',
  RouteDetailsView = 'web_route_details_view',
  RouteTaskDetails = 'route_task_details',
  RouteItinerary = 'route_itinerary',
  TaskSwitch = 'task_switch',
  ItineraryPopup = 'intinerary_pop_up',
  GoogleDirectionsEvent = 'google_directions',
  GrabNavigationEvent = 'grab_navigation_events',
  RoutesLoaded = 'routes_loaded',
  RouteTimingsChange = 'route_timings_change',
  RouteDateSelection = 'route_date_selection',
}

interface AnalyticsUser {
  name: string;
  email: string;
  impersonatingUserName: string;
  impersonatingUserId: string;
}

@Injectable({ providedIn: 'root' })
export class ProductAnalyticsService {
  private initialized = false;

  constructor(private settingsService: SettingsService) {
    this.init();
  }

  private async init(): Promise<void> {
    if (this.initialized) return;

    try {
      const res = await this.settingsService.getMixpanelSettings();

      if (!res || res.token == '') return;

      mixpanel.init(res.token, {
        debug: res.isDebug,
        persistence: res.persistence,
        autocapture: res.autoCapture,
        api_host: 'https://api-eu.mixpanel.com',
      });

      this.initialized = true;
    } catch (error) {
      console.error('Error initializing Mixpanel:', error);
    }
  }

  public async trackEvent(
    eventName: ProductAnalyticsEvent,
    props: Record<string, any> = {},
  ): Promise<void> {
    await this.ensureInit();
    try {
      mixpanel.track(eventName, {
        ...props,
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  public identifyUser(userId: string, user: AnalyticsUser): void {
    this.ensureInit();
    mixpanel.identify(userId);
    mixpanel.people.set({
      $name: user.name,
      $email: user.email,
    });
    mixpanel.register({
      isImpersonating: user.impersonatingUserId != null,
      impersonatingUserName: user.impersonatingUserName,
      impersonatingUserId: user.impersonatingUserId,
    });
  }

  private async ensureInit(): Promise<void> {
    if (!this.initialized) {
      return this.init();
    }
  }
}
